/**
 * Low-level cookie read/write for the session mirror.
 *
 * Hand-rolled rather than pulling in `js-cookie` (which legacy uses): the only
 * part of that library we would exercise is `encodeURIComponent` plus attribute
 * serialisation, and we need our own domain/secure resolution on top anyway. The
 * wire format is deliberately identical to js-cookie's, because legacy pages on
 * other *.amd.com hosts read these same cookies.
 *
 * Nothing here is a security boundary. These cookies are JS-readable by design —
 * see sessionMirror.js for what that buys and what it costs.
 */
import { isAmdHost } from '@utils/hosts';

/** Legacy's own lifetime: `expires: 1` day (Login.vue:155). */
const MAX_AGE_SECONDS = 24 * 60 * 60;

/**
 * The cookie domain, resolved from where the app is actually served.
 *
 * Legacy hardcodes `domain: 'amd.com'`, which a browser rejects outright when
 * the page is on localhost — so in local dev every one of its cookie writes
 * silently does nothing and the session never survives a reload. Scoping to
 * amd.com only when we are actually on amd.com keeps production byte-identical
 * while letting dev work.
 *
 * @returns {string} '' means host-only (omit the attribute entirely).
 */
export function cookieDomain() {
  const host = typeof window === 'undefined' ? '' : window.location.hostname;
  return isAmdHost(host) ? 'amd.com' : '';
}

/**
 * `Secure` is conditional for the same reason the domain is.
 *
 * A Secure cookie set over plain http is discarded by the cookie jar, so
 * hardcoding it (as legacy does) would make the mirror unreadable under
 * `pnpm dev` and in jsdom, where the tests run on http://localhost. Production
 * is https, so this resolves to exactly legacy's `secure: true` there.
 */
function isSecureContext() {
  return typeof window !== 'undefined' && window.location.protocol === 'https:';
}

/** The attribute tail shared by every write, so set and remove cannot drift. */
function attributes() {
  const domain = cookieDomain();
  return (
    '; path=/' +
    (domain ? `; domain=${domain}` : '') +
    '; samesite=strict' +
    (isSecureContext() ? '; secure' : '')
  );
}

/** Document access throws in some embedded contexts; auth must not die for it. */
function canUseCookies() {
  return typeof document !== 'undefined';
}

export function setCookie(name, value) {
  if (!canUseCookies()) return;
  // Skip rather than write the string "undefined" — legacy writes it, and the
  // reader then has to special-case a value that looks real but is not.
  if (value === undefined || value === null) return;
  try {
    document.cookie = `${name}=${encodeURIComponent(String(value))}; max-age=${MAX_AGE_SECONDS}${attributes()}`;
  } catch {
    // Blocked cookies. The in-memory session still works for this page view;
    // only surviving a reload is lost, which is not worth failing login over.
  }
}

/** @returns {string|null} decoded value, or null when absent. */
export function getCookie(name) {
  if (!canUseCookies()) return null;
  try {
    const prefix = `${name}=`;
    const hit = document.cookie
      .split(';')
      .map((part) => part.trim())
      .find((part) => part.startsWith(prefix));
    if (!hit) return null;
    return decodeURIComponent(hit.slice(prefix.length));
  } catch {
    return null;
  }
}

/**
 * Removal has to repeat path and domain or the browser keeps the cookie.
 *
 * Legacy gets this subtly wrong: it SETS with `domain: 'amd.com'` and REMOVES
 * with `domain: '.amd.com'` (apiservice.js:194). Using one resolver for both is
 * the whole reason `attributes()` exists.
 */
export function removeCookie(name) {
  if (!canUseCookies()) return;
  try {
    document.cookie = `${name}=; max-age=0${attributes()}`;
  } catch {
    // Nothing useful to do; the in-memory token is cleared regardless.
  }
}
